import React, { useState, useEffect, useCallback } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/Common/Loader';
import { API_URL } from '../../config';

interface UserSubscription {
    email: string;
    full_name: string;
    role: {
        name: string;
    };
    subscription_end_date: string;
}

const AdminSubscriptionsScreen: React.FC = () => {
    const { token } = useAuth();
    const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    // Form state
    const [email, setEmail] = useState('');
    const [roleName, setRoleName] = useState('paid_user_pro');

    // Mode: 'days' or 'date'
    const [mode, setMode] = useState<'days' | 'date'>('days');
    const [days, setDays] = useState(30);
    const [endDate, setEndDate] = useState('');

    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const fetchSubscriptions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/subscriptions`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setSubscriptions(data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchSubscriptions();
    }, [token, fetchSubscriptions]);

    const handleOpenModal = (user?: UserSubscription) => {
        setMessage(null);
        if (user) {
            setEmail(user.email);
            setRoleName(user.role.name);

            if (user.subscription_end_date) {
                setMode('date');
                // Format for input date: YYYY-MM-DD
                const d = new Date(user.subscription_end_date);
                setEndDate(d.toISOString().split('T')[0]);
            } else {
                setMode('days');
                setDays(30);
                setEndDate('');
            }
        } else {
            // New
            setEmail('');
            setRoleName('paid_user_pro');
            setMode('days');
            setDays(30);
            setEndDate('');
        }
        setShowModal(true);
    };

    const handleGrant = async (e: React.FormEvent) => {
        e.preventDefault();
        setProcessing(true);
        setMessage(null);

        try {
            const payload: any = {
                email,
                role_name: roleName
            };

            if (mode === 'days') {
                payload.days = Number(days);
            } else {
                payload.end_date = endDate; // YYYY-MM-DD
            }

            const response = await fetch(`${API_URL}/v1/admin/subscriptions/grant`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setMessage({ type: 'success', text: 'Souscription mise à jour avec succès !' });
                setShowModal(false);
                fetchSubscriptions();
            } else {
                const err = await response.json();
                setMessage({ type: 'error', text: err.detail || "Erreur lors de l'ajout" });
            }
        } catch (error) {
            setMessage({ type: 'error', text: "Erreur de connexion" });
        } finally {
            setProcessing(false);
        }
    };

    return (
        <SiteLayout>
            <Header icon='subscriptions' title='Gestion des Souscriptions' />

            <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                    <button
                        onClick={() => handleOpenModal()}
                        style={{
                            padding: '10px 20px',
                            background: '#2563eb',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                        }}
                    >
                        + Ajouter une souscription
                    </button>
                </div>

                {message && (
                    <div style={{
                        padding: '15px',
                        marginBottom: '20px',
                        borderRadius: '5px',
                        background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
                        color: message.type === 'success' ? '#166534' : '#991b1b',
                    }}>
                        {message.text}
                    </div>
                )}

                <Box>
                    <div style={{ padding: '20px' }}>
                        <h3 style={{ marginBottom: '20px' }}>Abonnements Actifs ({subscriptions.length})</h3>

                        {loading ? (
                            <Loader />
                        ) : subscriptions.length === 0 ? (
                            <p style={{ color: '#64748b', textAlign: 'center', padding: '20px' }}>Aucun abonnement actif trouvé.</p>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                                        <th style={{ padding: '12px' }}>Utilisateur</th>
                                        <th style={{ padding: '12px' }}>Rôle</th>
                                        <th style={{ padding: '12px' }}>Expire le</th>
                                        <th style={{ padding: '12px' }}>Statut</th>
                                        <th style={{ padding: '12px' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscriptions
                                        .sort((a, b) => {
                                            const aExp = a.subscription_end_date ? new Date(a.subscription_end_date) : null;
                                            const bExp = b.subscription_end_date ? new Date(b.subscription_end_date) : null;
                                            const aActive = !aExp || aExp > new Date();
                                            const bActive = !bExp || bExp > new Date();
                                            if (aActive && !bActive) return -1;
                                            if (!aActive && bActive) return 1;
                                            return 0;
                                        })
                                        .map((sub, index) => {
                                            const isPermanent = !sub.subscription_end_date;
                                            let endDateReal: Date | null = null;
                                            let diffDays = 0;

                                            if (!isPermanent) {
                                                endDateReal = new Date(sub.subscription_end_date);
                                                const now = new Date();
                                                const diffTime = endDateReal.getTime() - now.getTime();
                                                diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                            }

                                            return (
                                                <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '12px' }}>
                                                        <div style={{ fontWeight: 'bold' }}>{sub.full_name || 'Sans nom'}</div>
                                                        <div style={{ fontSize: '12px', color: '#64748b' }}>{sub.email}</div>
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        <span style={{
                                                            background: '#eff6ff',
                                                            color: '#1d4ed8',
                                                            padding: '2px 8px',
                                                            borderRadius: '10px',
                                                            fontSize: '12px',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {sub.role?.name}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        {isPermanent ? (
                                                            <span style={{ color: '#059669', fontWeight: 'bold' }}>Illimité</span>
                                                        ) : (
                                                            <div style={{ fontSize: '13px' }}>
                                                                {endDateReal?.toLocaleDateString('fr-FR', {
                                                                    day: 'numeric',
                                                                    month: 'short',
                                                                    year: 'numeric'
                                                                })}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        {isPermanent ? (
                                                            <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>ACTIF</span>
                                                        ) : diffDays > 0 ? (
                                                            <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>
                                                                {diffDays} JOURS
                                                            </span>
                                                        ) : (
                                                            <span style={{ background: '#fee2e2', color: '#991b1b', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold' }}>EXPIRÉ</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px' }}>
                                                        <button
                                                            onClick={() => handleOpenModal(sub)}
                                                            style={{
                                                                background: 'transparent',
                                                                border: '1px solid #cbd5e1',
                                                                borderRadius: '5px',
                                                                padding: '5px 10px',
                                                                cursor: 'pointer',
                                                                color: '#64748b'
                                                            }}
                                                        >
                                                            ✎ Éditer
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </Box>
            </div>

            {/* Modal */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'var(--bg-box)',
                        padding: '30px',
                        borderRadius: '10px',
                        width: '500px',
                        maxWidth: '90%',
                    }}>
                        <h2 style={{ marginBottom: '20px' }}>{email ? 'Modifier la souscription' : 'Nouvelle souscription'}</h2>
                        <form onSubmit={handleGrant}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Email Utilisateur</label>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="user@example.com"
                                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #cbd5e1' }}
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Plan (Rôle)</label>
                                <select
                                    value={roleName}
                                    onChange={e => setRoleName(e.target.value)}
                                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #cbd5e1' }}
                                >
                                    <option value="paid_user_pro">Kanea Pro (paid_user_pro)</option>
                                    <option value="paid_user">Kanea Standard (paid_user)</option>
                                    <option value="basic_user">Gratuit (basic_user)</option>
                                    <option value="demo_user">🔒 Démo (demo_user)</option>
                                    <option value="global_admin">Admin (global_admin)</option>
                                </select>
                            </div>

                            <div style={{ marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                                <label style={{ fontWeight: 'bold', marginRight: '15px' }}>Type de durée :</label>
                                <label style={{ marginRight: '15px', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="mode"
                                        value="days"
                                        checked={mode === 'days'}
                                        onChange={() => setMode('days')}
                                    /> Jours
                                </label>
                                <label style={{ cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="mode"
                                        value="date"
                                        checked={mode === 'date'}
                                        onChange={() => setMode('date')}
                                    /> Date précise
                                </label>
                            </div>

                            {mode === 'days' ? (
                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Durée (Jours à partir d'aujourd'hui)</label>
                                    <input
                                        type="number"
                                        value={days}
                                        onChange={e => setDays(Number(e.target.value))}
                                        style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #cbd5e1' }}
                                    />
                                    <small style={{ color: '#64748b' }}>0 = Illimité</small>
                                </div>
                            ) : (
                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Date de fin</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={e => setEndDate(e.target.value)}
                                        style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #cbd5e1' }}
                                    />
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{ padding: '10px 20px', background: '#e2e8f0', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={processing}
                                    style={{
                                        padding: '10px 20px',
                                        background: processing ? '#93c5fd' : '#2563eb',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: processing ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {processing ? 'Enregistrer' : 'Valider'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </SiteLayout>
    );
};

export default AdminSubscriptionsScreen;

